import { createStore, Effect, Event } from 'effector';

import { ONE_DAY_IN_SECONDS } from '@m/constants/Time';
import { applyReducers, DoneHandler } from '@m/effector-utils';
import { ParkingPass } from '@m/types/api/ParkingPass';
import {
  PassDiscount,
  PassPlanConfiguration,
  PassPlanPreview,
} from '@m/types/api/pass/SelfServiceParkingPassPlan';
import { HydratedValidationQrView } from '@m/types/api/Validation/qr/ValidationQRScan';
import { formatGrpcToCurrency, subtractGrpcCurrencies } from '@m/utils/grpc/formatGRPCDollar';
import { APIResponse } from '@m/utils/http';

import {
  fetchPassPreview,
  setSelectedPlanConfig,
  removeDiscount,
  setScannedDiscountUuid,
  setSuccessfulActiveVisitDiscountScan,
  setPendingPassValidation,
  setSelectedActivePass,
} from './actions';

type PassesStoreState = {
  discountDetails: PassDiscount | null;
  discountedPricePerDay: number | null;
  maxDurationDays: number | null;
  passConfigOptions: Record<number, PassPlanConfiguration>;
  pendingPassValidation: HydratedValidationQrView | null | undefined;
  scannedDiscountUuid: string;
  selectedActivePass: ParkingPass | null | undefined;
  selectedPlanConfig: PassPlanConfiguration | null;
  selectedPlanUuid: string;
  successfulActiveVisitDiscountScan: boolean | null;
};

const initialState = {
  pendingPassValidation: null,
  scannedDiscountUuid: '',
  successfulActiveVisitDiscountScan: null,
  maxDurationDays: null,
  selectedActivePass: null,
  selectedPlanUuid: '',
  selectedPlanConfig: null,
  passConfigOptions: {},
  discountDetails: null,
  discountedPricePerDay: null,
};

const store = createStore<PassesStoreState>(initialState);

type Reducers = {
  fetchPassPreview: {
    action: Effect<
      Parameters<typeof fetchPassPreview>[0],
      APIResponse<{ passPlanConfigurations: Record<number, PassPlanPreview> }>
    >;
    done: DoneHandler<
      Parameters<typeof fetchPassPreview>[0],
      PassesStoreState,
      APIResponse<{ passPlanConfigurations: Record<number, PassPlanPreview> }>
    >;
  };
  removeDiscount: {
    action: Event<void>;
    reducer: (state: PassesStoreState) => PassesStoreState;
  };
  setPendingPassValidation: {
    action: Event<HydratedValidationQrView | null | undefined>;
    reducer: (
      state: PassesStoreState,
      payload: HydratedValidationQrView | null | undefined,
    ) => PassesStoreState;
  };
  setScannedDiscountUuid: {
    action: Event<{ discountUuid: string }>;
    reducer: (state: PassesStoreState, payload: { discountUuid: string }) => PassesStoreState;
  };
  setSelectedActivePass: {
    action: Event<ParkingPass | null | undefined>;
    reducer: (state: PassesStoreState, payload: ParkingPass | null | undefined) => PassesStoreState;
  };
  setSelectedPlanConfig: {
    action: Event<{ config: PassPlanConfiguration }>;
    reducer: (
      state: PassesStoreState,
      payload: { config: PassPlanConfiguration },
    ) => PassesStoreState;
  };
  setSuccessfulActiveVisitDiscountScan: {
    action: Event<boolean | null>;
    reducer: (state: PassesStoreState, payload: boolean) => PassesStoreState;
  };
};

export const reducers: Reducers = {
  fetchPassPreview: {
    action: fetchPassPreview,
    done: (state, { result: { success, data } }) => {
      if (success && data) {
        const passPlan = data.passPlanConfigurations?.[0];
        if (!passPlan?.configurations) {
          return state;
        }

        /*
         * For each preview fetched, only one period is returned.
         * We can safely assume that the first one is the only one,
         * and it is always a number.
         */
        const configPeriods = Object.keys(passPlan.configurations);
        if (configPeriods.length === 0) {
          return state;
        }
        const newConfigPeriod = parseInt(configPeriods[0], 10);
        const newConfig = passPlan.configurations[newConfigPeriod];
        if (!newConfig) {
          return state;
        }
        const newConfigOption = { [newConfigPeriod]: newConfig };
        let filteredConfigOptions;
        let discountedPricePerDay;

        /*
         * If a pass preview is fetched with a discount, but other saved options do not have a discount,
         * the state of the options is reset to ensure the latest discount is fetched.
         */
        if (passPlan.discount) {
          filteredConfigOptions = Object.fromEntries(
            Object.entries(state.passConfigOptions).filter(([_, config]) => {
              const discountAmount = formatGrpcToCurrency(config.pricing.discount);
              return discountAmount > 0;
            }),
          );

          /*
           * Check if we've already calculated the discounted price per day.
           * This is to avoid recalculating it unnecessarily for the same plan.
           * If so, use it, otherwise calculate it based on the new configuration.
           */
          if (
            state.selectedPlanUuid === passPlan.planUuid &&
            state.discountedPricePerDay !== null &&
            state.discountedPricePerDay !== undefined
          ) {
            discountedPricePerDay = state.discountedPricePerDay;
          } else {
            discountedPricePerDay =
              formatGrpcToCurrency(
                subtractGrpcCurrencies(newConfig.pricing.basePrice, newConfig.pricing.discount),
              ) / newConfigPeriod;
          }
        } else {
          filteredConfigOptions =
            state.selectedPlanUuid === passPlan.planUuid ? state.passConfigOptions : {};
        }

        return {
          ...state,
          maxDurationDays: parseInt(passPlan.maxDuration.replace('s', ''), 10) / ONE_DAY_IN_SECONDS,
          selectedPlanUuid: passPlan.planUuid,
          selectedPlanConfig: newConfig,
          passConfigOptions: {
            ...filteredConfigOptions,
            ...newConfigOption,
          },
          discountDetails: passPlan?.discount ?? null,
          discountedPricePerDay: discountedPricePerDay ?? null,
        };
      }

      return state;
    },
  },
  setSelectedPlanConfig: {
    action: setSelectedPlanConfig,
    reducer: (state, { config }) => ({
      ...state,
      selectedPlanConfig: config,
    }),
  },
  removeDiscount: {
    action: removeDiscount,
    reducer: (state) => ({
      ...state,
      discountDetails: null,
      discountedPricePerDay: null,
      passConfigOptions: {},
    }),
  },
  setScannedDiscountUuid: {
    action: setScannedDiscountUuid,
    reducer: (state, { discountUuid }) => ({
      ...state,
      scannedDiscountUuid: discountUuid,
    }),
  },
  setSuccessfulActiveVisitDiscountScan: {
    action: setSuccessfulActiveVisitDiscountScan,
    reducer: (state, payload) => ({
      ...state,
      successfulActiveVisitDiscountScan: payload,
    }),
  },
  setPendingPassValidation: {
    action: setPendingPassValidation,
    reducer: (state, payload) => ({
      ...state,
      pendingPassValidation: payload,
    }),
  },
  setSelectedActivePass: {
    action: setSelectedActivePass,
    reducer: (state, payload) => ({
      ...state,
      selectedActivePass: payload,
    }),
  },
};

export default applyReducers({ storeName: 'customer/passes', store, reducers });
